import { useCallback } from 'react';

import { ColorModel, MaterialModel, StoreModel } from '@common/models';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { updatePrevSelectedColor, updateSelectedColor } from '@redux/slices/colorSlice';
import { updatePrevSelectedMaterial, updateSelectedMaterial } from '@redux/slices/materialSlice';
import { updatePrevSelectedStore, updateSelectedStore } from '@redux/slices/storeSlice';
import { selectZipcode, updatePrevZipcode, updateZipcode } from '@redux/slices/zipcodeSlice';

type UseUpdateWithHistoryReturn = {
    setSelectedZipCodeWithHistory: (newZipCode: string) => void;
    setSelectedStoreWithHistory: (newStore: StoreModel) => void;
    setSelectedColorWithHistory: (newColor: ColorModel) => void;
    setSelectedMaterialWithHistory: (newColor: MaterialModel) => void;
};

const useUpdateWithHistory = (): UseUpdateWithHistoryReturn => {
    const dispatch = useAppDispatch();
    const zipCode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedColor } = useSelectedColor();

    const setSelectedColorWithHistory = useCallback(
        (newColor: ColorModel): void => {
            dispatch(updatePrevSelectedColor(selectedColor));
            dispatch(updateSelectedColor(newColor));
        },
        [dispatch, selectedColor]
    );

    const setSelectedMaterialWithHistory = useCallback(
        (newMaterial: MaterialModel): void => {
            dispatch(updatePrevSelectedColor(selectedColor));
            dispatch(updatePrevSelectedMaterial(selectedMaterial));
            dispatch(updateSelectedMaterial(newMaterial));
        },
        [dispatch, selectedColor, selectedMaterial]
    );

    const setSelectedStoreWithHistory = useCallback(
        (newStore: StoreModel): void => {
            dispatch(updatePrevSelectedColor(selectedColor));
            dispatch(updatePrevSelectedMaterial(selectedMaterial));
            dispatch(updatePrevSelectedStore(selectedStore));
            dispatch(updateSelectedStore(newStore));
        },
        [dispatch, selectedColor, selectedMaterial, selectedStore]
    );
    const setSelectedZipCodeWithHistory = useCallback(
        (newZipCode: string): void => {
            dispatch(updatePrevSelectedColor(selectedColor));
            dispatch(updatePrevSelectedMaterial(selectedMaterial));
            dispatch(updatePrevSelectedStore(selectedStore));
            dispatch(updatePrevZipcode(zipCode));
            dispatch(updateZipcode(newZipCode));
        },
        [dispatch, selectedColor, selectedMaterial, selectedStore, zipCode]
    );

    return {
        setSelectedStoreWithHistory,
        setSelectedColorWithHistory,
        setSelectedZipCodeWithHistory,
        setSelectedMaterialWithHistory,
    };
};

export default useUpdateWithHistory;
